import { z } from "zod";

import { ApiError, apiDelete, apiGet, apiPost, apiPut } from "../lib/api";

// ─── Group records ────────────────────────────────────────────────────────────
//
// Target Groups (Phase 2): reusable, customer-scoped named sets of targets.
// Mirrors console_api/schemas/group.py. List + detail rows are identical
// (group_id/customer_id/name/description/member_count/created_at/created_by).

export const GroupListItemSchema = z.object({
  group_id: z.string(),
  customer_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  member_count: z.number(),
  created_at: z.string(),
  created_by: z.string(),
});
export type GroupListItem = z.infer<typeof GroupListItemSchema>;

export const GroupDetailSchema = z.object({
  group_id: z.string(),
  customer_id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  member_count: z.number(),
  created_at: z.string(),
  created_by: z.string(),
});
export type GroupDetail = z.infer<typeof GroupDetailSchema>;

// A membership row enriched from the global target registry (ids that no longer
// resolve are dropped server-side, so every item here is a live target).
export const GroupMemberItemSchema = z.object({
  target_id: z.string(),
  full_name: z.string(),
  primary_phone_e164: z.string().nullable(),
  role: z.string().nullable(),
  department: z.string().nullable(),
});
export type GroupMemberItem = z.infer<typeof GroupMemberItemSchema>;

// ─── Response wrappers ──────────────────────────────────────────────────────────

export const GroupListResponseSchema = z.object({
  items: z.array(GroupListItemSchema),
  total: z.number(),
});
export type GroupListResponse = z.infer<typeof GroupListResponseSchema>;

// create/get/update return the record wrapped: { record: <group> }.
export const GroupDetailResponseSchema = z.object({
  record: GroupDetailSchema,
});
export type GroupDetailResponse = z.infer<typeof GroupDetailResponseSchema>;

export const GroupMembersResponseSchema = z.object({
  items: z.array(GroupMemberItemSchema),
  total: z.number(),
});
export type GroupMembersResponse = z.infer<typeof GroupMembersResponseSchema>;

// ─── Campaign group-sync result ─────────────────────────────────────────────────
//
// POST /api/campaigns/{id}/sync-group full-mirrors the linked group's members
// into the campaign target pool, reporting what it touched / skipped.

export const SyncGroupResponseSchema = z.object({
  added: z.number(),
  removed: z.number(),
  skipped_test_only: z.number(),
  skipped_no_phone: z.number(),
  skipped_missing: z.number(),
});
export type SyncGroupResponse = z.infer<typeof SyncGroupResponseSchema>;

// ─── Write bodies ────────────────────────────────────────────────────────────────

export interface GroupCreateBody {
  customer_id: string;
  name: string;
  description?: string | null;
}

export interface GroupUpdateBody {
  name?: string;
  description?: string | null;
}

// ─── Group CRUD fetchers ──────────────────────────────────────────────────────────

export function fetchGroups() {
  return apiGet("/api/groups", GroupListResponseSchema);
}

export async function createGroup(body: GroupCreateBody) {
  const { record } = await apiPost(
    "/api/groups",
    GroupDetailResponseSchema,
    body,
  );
  return record;
}

export async function fetchGroup(id: string) {
  const { record } = await apiGet(
    `/api/groups/${id}`,
    GroupDetailResponseSchema,
  );
  return record;
}

export async function updateGroup(id: string, body: GroupUpdateBody) {
  const { record } = await apiPut(
    `/api/groups/${id}`,
    GroupDetailResponseSchema,
    body,
  );
  return record;
}

export function deleteGroup(id: string) {
  return apiDelete(`/api/groups/${id}`);
}

// ─── Membership fetchers ───────────────────────────────────────────────────────────

export function fetchGroupMembers(id: string) {
  return apiGet(`/api/groups/${id}/members`, GroupMembersResponseSchema);
}

export function addGroupMembers(id: string, target_ids: string[]) {
  return apiPost(
    `/api/groups/${id}/members`,
    GroupMembersResponseSchema,
    { target_ids },
  );
}

// DELETE /members carries a JSON body (target_ids) and returns the refreshed
// member list. The shared apiDelete helper sends no body, so this one does a
// schema-validated DELETE-with-body inline (mirrors lib/api.ts request()).
export async function removeGroupMembers(id: string, target_ids: string[]) {
  const BASE = import.meta.env.VITE_API_BASE_URL ?? "";
  const res = await fetch(`${BASE}/api/groups/${id}/members`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_ids }),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const errBody = await res.json();
      detail = errBody.detail ?? detail;
    } catch {
      // body was not JSON; keep statusText
    }
    throw new ApiError(res.status, detail, res);
  }
  return GroupMembersResponseSchema.parse(await res.json());
}

// ─── Campaign sync ──────────────────────────────────────────────────────────────────

export function syncCampaignToGroup(campaignId: string) {
  return apiPost(
    `/api/campaigns/${campaignId}/sync-group`,
    SyncGroupResponseSchema,
    undefined,
  );
}
